import { useMemo, useState } from 'react'
import { MarkerType } from '@xyflow/react'
import type { OntologyFlowEdge } from '@/frontend/features/Fde/OntologyGraphEdge'
import type { OntologyFlowNode } from '@/frontend/features/Fde/OntologyGraphNode'
import { groupForFamily } from '@/frontend/features/Fde/ontologyGraphGroups'
import { elementImageUrl, elementSubtitle } from '@/frontend/features/Fde/ontologyProperties'
import { defaultOntologyAnchors, projectOntologyGraph, type GraphDepth, type GraphPerspective } from '@/frontend/features/Fde/ontologyGraphPerspective'
import { ontologyNodeSize, useOntologyGraphLayout } from '@/frontend/features/Fde/useOntologyGraphLayout'
import type { FdeCopy, ModelElement, Relationship } from '@/frontend/features/Fde/types'

interface Props {
  elements: ModelElement[]
  relationships: Relationship[]
  copy: FdeCopy
  initialFocusId?: string | null
}

function matchesQuery(element: ModelElement, query: string) {
  if (!query) return true
  const searchable = `${element.name} ${element.ontology_class?.name || element.kind} ${element.ontology_class?.family.name || ''} ${element.description || ''}`
  return searchable.toLocaleLowerCase().includes(query)
}

export default function useOntologyGraphView({ elements, relationships, copy, initialFocusId }: Props) {
  const availableInitialFocusId = initialFocusId && elements.some((element) => String(element.id) === initialFocusId) ? initialFocusId : null
  const [query, setQuery] = useState('')
  const [perspective, setPerspective] = useState<GraphPerspective>(availableInitialFocusId ? 'free' : 'processes')
  const [depth, setDepth] = useState<GraphDepth>(1)
  const [manualAnchorIds, setManualAnchorIds] = useState<string[]>(availableInitialFocusId ? [availableInitialFocusId] : [])
  const [showGaps, setShowGaps] = useState(false)
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [selectedRelationshipId, setSelectedRelationshipId] = useState<string | null>(null)
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null)
  const [pinnedHighlightNodeId, setPinnedHighlightNodeId] = useState<string | null>(null)
  const effectiveAnchorIds = useMemo(() => {
    const availableIds = new Set(elements.map((element) => String(element.id)))
    if (perspective === 'free') return manualAnchorIds.filter((id) => availableIds.has(id))
    return defaultOntologyAnchors(elements, relationships, perspective)
  }, [elements, manualAnchorIds, perspective, relationships])
  const projection = useMemo(
    () => projectOntologyGraph({ elements, relationships, anchorIds: effectiveAnchorIds, depth, showGaps }),
    [depth, effectiveAnchorIds, elements, relationships, showGaps],
  )
  const layoutAnchorIds = useMemo(() => showGaps ? [] : effectiveAnchorIds, [effectiveAnchorIds, showGaps])
  const layout = useOntologyGraphLayout(projection.elements, projection.relationships, layoutAnchorIds)
  const normalizedQuery = query.trim().toLocaleLowerCase()
  const anchorElements = effectiveAnchorIds.flatMap((id) => elements.find((element) => String(element.id) === id) || [])
  const selectedNode = selectedNodeId ? projection.elements.find((element) => String(element.id) === selectedNodeId) : undefined
  const selectedRelationship = selectedRelationshipId ? projection.relationships.find((relationship) => String(relationship.id) === selectedRelationshipId) : undefined
  const selectedNodeRelationships = selectedNode
    ? relationships.filter((relationship) => String(relationship.from.id) === selectedNodeId || String(relationship.to.id) === selectedNodeId)
    : []
  const highlightedNodeId = selectedNodeId || pinnedHighlightNodeId || hoveredNodeId
  const highlightedRelationshipId = selectedRelationshipId
  const highlightedNodeRelationships = highlightedNodeId
    ? projection.relationships.filter((relationship) => String(relationship.from.id) === highlightedNodeId || String(relationship.to.id) === highlightedNodeId)
    : []
  const highlightedRelationship = highlightedRelationshipId
    ? projection.relationships.find((relationship) => String(relationship.id) === highlightedRelationshipId)
    : undefined

  const flowNodes = useMemo<OntologyFlowNode[]>(() => layout.nodes.map(({ element, position }) => {
    const id = String(element.id)
    const relatedToNode = !highlightedNodeId || id === highlightedNodeId || highlightedNodeRelationships.some((relationship) => String(relationship.from.id) === id || String(relationship.to.id) === id)
    const relatedToRelationship = !highlightedRelationship || String(highlightedRelationship.from.id) === id || String(highlightedRelationship.to.id) === id
    return {
      id,
      type: 'ontology',
      position,
      width: ontologyNodeSize.width,
      height: ontologyNodeSize.height,
      ariaLabel: element.name,
      ariaRole: 'button',
      data: {
        name: element.name,
        className: element.ontology_class?.name || element.kind,
        imageUrl: elementImageUrl(element),
        subtitle: elementSubtitle(element),
        group: groupForFamily(element.ontology_class?.family.key),
        connectionCount: projection.connectionCounts.get(id) || 0,
        status: element.status,
        active: id === highlightedNodeId,
        anchor: effectiveAnchorIds.includes(id) && !showGaps,
        shared: projection.sharedElementIds.has(id),
        dimmed: !(relatedToNode && relatedToRelationship && matchesQuery(element, normalizedQuery)),
        orientation: layout.orientation,
        relationshipLabel: copy.workspace.relationships.toLocaleLowerCase(),
        anchorLabel: copy.workspace.graph_anchor,
        sharedLabel: copy.workspace.graph_shared,
      },
    }
  }), [copy, effectiveAnchorIds, highlightedNodeId, highlightedNodeRelationships, highlightedRelationship, layout.nodes, layout.orientation, normalizedQuery, projection.connectionCounts, projection.sharedElementIds, showGaps])

  const flowEdges = useMemo<OntologyFlowEdge[]>(() => layout.edges.map(({ relationship, points }) => {
    const id = String(relationship.id)
    const active = id === highlightedRelationshipId || String(relationship.from.id) === highlightedNodeId || String(relationship.to.id) === highlightedNodeId
    const queryMatches = !normalizedQuery || [relationship.from.id, relationship.to.id].some((relatedId) => {
      const element = projection.elements.find((item) => String(item.id) === String(relatedId))
      return element && matchesQuery(element, normalizedQuery)
    })
    return {
      id,
      source: String(relationship.from.id),
      target: String(relationship.to.id),
      type: 'ontology',
      ariaLabel: `${relationship.from.name} ${relationship.verb} ${relationship.to.name}`,
      markerEnd: { type: MarkerType.ArrowClosed, color: active ? 'var(--primary)' : 'var(--muted-foreground)', width: active ? 18 : 14, height: active ? 18 : 14 },
      data: {
        points,
        active,
        dimmed: Boolean((highlightedNodeId || highlightedRelationshipId) && !active) || !queryMatches,
        number: selectedNode ? selectedNodeRelationships.findIndex((item) => item.id === relationship.id) + 1 : null,
      },
    }
  }), [highlightedNodeId, highlightedRelationshipId, layout.edges, normalizedQuery, projection.elements, selectedNode, selectedNodeRelationships])

  function clearSelection() {
    setSelectedNodeId(null)
    setSelectedRelationshipId(null)
  }

  function choosePerspective(value: GraphPerspective) {
    setPerspective(value)
    setDepth(value === 'organization' ? 2 : 1)
    setManualAnchorIds([])
    setPinnedHighlightNodeId(null)
    setShowGaps(false)
    clearSelection()
  }

  function togglePin(id: string) {
    if (perspective !== 'free') {
      setPerspective('free')
      setManualAnchorIds([id])
      return
    }
    setManualAnchorIds((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id].slice(0, 3))
  }

  function focusNode(id: string) {
    setPerspective('free')
    setManualAnchorIds([id])
    setDepth(1)
    setShowGaps(false)
    setSelectedNodeId(id)
    setSelectedRelationshipId(null)
  }

  function showRelated(id: string) {
    setPerspective('free')
    setManualAnchorIds([id])
    setPinnedHighlightNodeId(id)
    setDepth(1)
    setShowGaps(false)
    clearSelection()
  }

  function toggleHighlight(id: string) {
    setPinnedHighlightNodeId((current) => current === id ? null : id)
  }

  return {
    query, setQuery, perspective, depth, setDepth, showGaps, projection, layout, anchorElements,
    selectedNode, selectedRelationship, selectedNodeRelationships, flowNodes, flowEdges,
    manuallyPinned: Boolean(selectedNodeId && perspective === 'free' && manualAnchorIds.includes(selectedNodeId)),
    pinLimitReached: manualAnchorIds.length >= 3,
    pinnedHighlightNodeId, choosePerspective, togglePin, focusNode, showRelated, toggleHighlight, clearSelection,
    hoverNode: setHoveredNodeId,
    showGapView: () => { setShowGaps(true); clearSelection() },
    leaveGapView: () => { setShowGaps(false); clearSelection() },
    selectNode: (id: string) => { setSelectedNodeId((current) => current === id ? null : id); setSelectedRelationshipId(null) },
    selectRelationship: (id: string) => { setSelectedRelationshipId((current) => current === id ? null : id); setSelectedNodeId(null) },
  }
}
